import type { CreateEntryInput, GetEntryOutput, TEntry, TExhibit, TShow } from '@schemas'
import type { CheckedInExhibit, PendingIntakeEntry, useEntryIntakeManager } from './useEntryIntake'
import { parseApiErrorPayload, parseApiErrorStatus } from '@/lib/api-errors'

type SyncOptions = {
  intake: ReturnType<typeof useEntryIntakeManager>
  id: string
  showId: string
  showStatus?: TShow['status'] | null
  create: (request: CreateEntryInput) => Promise<TEntry>
  checkIn: (entryId: string) => Promise<TExhibit>
  read: (entryId: string) => Promise<GetEntryOutput>
}

export const PENDING_SYNC_DEFERRED_MESSAGE =
  'Entry saved on this device. Sync this entrant’s other pending entries first.'

export const PENDING_DUPLICATE_MESSAGE = 'This entrant already has an active entry in this class.'

// Finished attempts wait for review, and a closed show only replays requests
// it already captured.
export const canSyncPendingEntry = (entry: PendingIntakeEntry, showAllowsIntake: boolean) =>
  !entry.syncAttempt?.completed && (showAllowsIntake || Boolean(entry.syncAttempt))

/** Syncing everything leaves duplicates waiting for staff; only a sync of the entry itself retries one. */
export const syncsWithAll = (entry: PendingIntakeEntry, showAllowsIntake: boolean) =>
  entry.status !== 'duplicate' && canSyncPendingEntry(entry, showAllowsIntake)

/** Both foreground intake and background recovery execute this durable operation. */
export async function syncPendingEntry({
  intake,
  id,
  showId,
  showStatus,
  create,
  checkIn,
  read,
}: SyncOptions) {
  const canStart = showStatus === 'accepting_entries'
  const pending = await intake.preparePendingSync(id, canStart)
  if (!pending) return null
  const attempt = pending.syncAttempt
  // Preparation leaves a startable entry untouched only while it waits behind
  // this entrant's other unsent claims, or while it waits for staff as a duplicate.
  if (!attempt && canStart && pending.status !== 'duplicate') return 'deferred' as const
  let creating = false
  try {
    if (!attempt) {
      throw new Error(
        pending.status === 'duplicate'
          ? (pending.error ?? PENDING_DUPLICATE_MESSAGE)
          : 'Syncing entries is unavailable once intake closes.'
      )
    }
    if (attempt.request.showId !== showId)
      throw new Error('This pending entry belongs to another show.')
    if (attempt.completed)
      throw new Error('Earlier details were saved. Review the saved entry before retrying.')

    const resumingCreation = Boolean(attempt.createdEntry)
    let entry = attempt.createdEntry
    if (!entry) {
      // A saved request may be replayed after intake closes: the server resolves
      // committed submission IDs before its phase guard. New requests stay blocked.
      creating = true
      entry = await create(attempt.request)
      creating = false
      if (!entry.id) throw new Error('Entry response missing saved entry details.')
      if (!(await intake.savePendingCreation(id, attempt.id, entry))) return null
    }
    const entryId = entry.id
    if (!entryId) throw new Error('Entry response missing saved entry details.')

    let resolvedClassName: string | undefined = pending.className
    let resolvedClassCode: string | undefined
    const readEntry = async () => {
      const { entry: saved } = await read(entryId)
      if (
        saved.id !== entryId ||
        saved.showId !== showId ||
        saved.exhibitorId !== attempt.request.exhibitorId
      ) {
        throw new Error('Unable to verify the saved entry for this pending submission.')
      }
      resolvedClassCode = saved.class?.code
      resolvedClassName =
        saved.class?.name ?? (saved.classId === pending.classId ? pending.className : undefined)
      return saved
    }

    let checkedIn: CheckedInExhibit | undefined
    if (attempt.completionIntent === 'create_and_check_in') {
      let exhibitId: string | undefined
      if (resumingCreation) {
        const saved = await readEntry()
        entry = saved
        exhibitId = saved.exhibit?.id
      }
      if (!exhibitId) {
        if (!canStart)
          throw new Error('Entry saved, but check-in is unavailable once intake closes.')
        try {
          const exhibit = await checkIn(entryId)
          if (!exhibit.id || exhibit.entryId !== entryId || exhibit.showId !== showId) {
            throw new Error('Check-in response missing matching exhibit details.')
          }
          exhibitId = exhibit.id
        } catch (error) {
          // A lost response or concurrent tab can follow a committed check-in.
          // Neither a generic conflict nor a failed read proves completion.
          const saved = await readEntry().catch(() => {
            throw error
          })
          if (!saved.exhibit?.id) throw error
          entry = saved
          exhibitId = saved.exhibit.id
        }
      }
      checkedIn = {
        exhibitId,
        entryId,
        entryCode: entry.entryCode,
        classId: entry.classId ?? attempt.request.classId,
        className: resolvedClassName,
        classCode: resolvedClassCode,
        exhibitorId: attempt.request.exhibitorId,
        exhibitorName: pending.exhibitor.name,
        checkedInAt: new Date().toISOString(),
        remappedFrom:
          pending.rejectedCode ?? (pending.code !== entry.entryCode ? pending.code : null),
      }
      await intake.recordCheckedInExhibit(checkedIn)
    }
    await intake.recordRecentEntry({
      id: entryId,
      entryCode: entry.entryCode,
      exhibitorId: attempt.request.exhibitorId,
      className: resolvedClassName,
      originalCode: pending.rejectedCode ?? pending.code,
    })
    const finished = await intake.finishPendingSync(id, attempt.editVersion, entryId)
    // Label printing and the next entry do not wait for the refreshed history.
    void intake.refreshAssignmentContext(attempt.request.exhibitorId).catch(() => undefined)
    return { entry, pending, checkedIn, finished }
  } catch (error) {
    const payload = parseApiErrorPayload(error)
    const message =
      payload?.message ?? (error instanceof Error ? error.message : 'Unable to sync pending entry')
    if (
      creating &&
      attempt &&
      ((parseApiErrorStatus(error) === 409 &&
        [
          'duplicate_entry',
          'allocation_order_conflict',
          'exhibit_id_conflict',
          'invalid_phase',
          'identifier_exhausted',
        ].includes(payload?.status ?? '')) ||
        (parseApiErrorStatus(error) === 403 && payload?.status === 'staff_exhibiting_disabled'))
    ) {
      // These explicit rejections did not create an entry. Allow the user to
      // remove the queued item or amend it before capturing a new request.
      await intake.rejectPendingCreation(id, attempt.id, {
        status: payload?.status === 'duplicate_entry' ? 'duplicate' : 'error',
        error: message,
        canReplaceCode: ['allocation_order_conflict', 'exhibit_id_conflict'].includes(
          payload?.status ?? ''
        ),
        existingEntryId:
          typeof payload?.existingEntryId === 'string'
            ? payload.existingEntryId
            : pending.existingEntryId,
      })
    } else {
      await intake.updatePending(id, {
        status: pending.status === 'duplicate' && !attempt ? 'duplicate' : 'error',
        error: message,
        canReplaceCode: false,
      })
    }
    throw error
  }
}
